/**
 * MA-ARCH-188-V4: Strategy Context Utilities
 *
 * Builds a strict, typed StrategyContext from the current profit path draft.
 * Used by AIFieldAssist, MessagingSynthesizer, and all downstream generators.
 */

/**
 * Deep-normalize biased fields: { value, source } → value
 * Also handles arrays and nested objects recursively.
 */
const normalize = (input) => {
  if (input === null || input === undefined) return input;
  if (typeof input !== "object") return input;
  if (
    typeof input === "object" &&
    input !== null &&
    "value" in input &&
    "source" in input &&
    (input.source === "entry_bias" || input.source === "user")
  ) {
    return normalize(input.value);
  }
  if (Array.isArray(input)) return input.map((item) => normalize(item));
  const result = {};
  for (const key in input) {
    if (key.startsWith("_")) continue;
    if (!Object.prototype.hasOwnProperty.call(input, key)) continue;
    result[key] = normalize(input[key]);
  }
  return result;
};

const str = (val) => {
  const v = normalize(val);
  return typeof v === "string" ? v : "";
};

/**
 * Build a strict StrategyContext from the current draft.
 * Every key is always present. Empty string for unfilled fields.
 */
export const buildStrategyContext = (draft) => {
  const ctx = draft?.builder_data?.context || {};
  const fdn = draft?.builder_data?.foundation || {};
  const aud = draft?.builder_data?.audience || {};
  const ofr = draft?.builder_data?.offer || {};
  const msg = draft?.builder_data?.messaging || null;

  return {
    context: {
      workspace: str(ctx.workspace),
      vertical: str(ctx.vertical),
      channel: str(ctx.channel),
      platform: str(ctx.platform),
      campaign_name: str(ctx.campaign_name),
      campaign_ref: str(ctx.campaign_ref),
    },
    foundation: {
      name: str(draft?.name),
      strategic_objective: str(fdn.strategic_objective),
      core_strategy: str(fdn.core_strategy),
    },
    audience: {
      primary_audience: str(aud.primary_audience),
      situation: str(aud.situation),
      core_problem: str(aud.core_problem),
      emotional_driver: str(aud.emotional_driver),
      awareness_stage: str(aud.awareness_stage),
      trigger_event: str(aud.trigger_event),
    },
    offer: {
      offer_name: str(ofr.offer_name),
      offer_type: str(ofr.offer_type),
      core_promise: str(ofr.core_promise),
      primary_benefit: str(ofr.primary_benefit),
      differentiation: str(ofr.differentiation),
      trust_proof: str(ofr.trust_proof),
      primary_cta: str(ofr.primary_cta),
      urgency: str(ofr.urgency),
    },
    messaging: msg
      ? {
          version: msg.version ?? 0,
          source: msg.source ?? "user",
          generated_at: msg.generated_at ?? null,
          fields: msg.fields
            ? {
                angle: str(msg.fields.angle),
                thesis: str(msg.fields.thesis),
                unique_mechanism: str(msg.fields.unique_mechanism),
                message_enemy: str(msg.fields.message_enemy),
                primary_hook: str(msg.fields.primary_hook),
                scroll_stopper: str(msg.fields.scroll_stopper),
                primary_emotion: str(msg.fields.primary_emotion),
                emotional_outcome: str(msg.fields.emotional_outcome),
                emotional_narrative: str(msg.fields.emotional_narrative),
                tone: str(msg.fields.tone),
                voice_notes: str(msg.fields.voice_notes),
                core_promise: str(msg.fields.core_promise),
                primary_pain: str(msg.fields.primary_pain),
                desired_outcome: str(msg.fields.desired_outcome),
                proof_angle: str(msg.fields.proof_angle),
                cta_framing: str(msg.fields.cta_framing),
              }
            : {
                angle: "",
                thesis: "",
                unique_mechanism: "",
                message_enemy: "",
                primary_hook: "",
                scroll_stopper: "",
                primary_emotion: "",
                emotional_outcome: "",
                emotional_narrative: "",
                tone: "",
                voice_notes: "",
                core_promise: "",
                primary_pain: "",
                desired_outcome: "",
                proof_angle: "",
                cta_framing: "",
              },
        }
      : null,
  };
};
